(() => {
  'use strict';
  const tag = 'faf-november-doorbusters';
  if (!window.customElements || customElements.get(tag)) return;

  const detroitTime = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Detroit', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });

  class NovemberDoorbusters extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-db-rail]');
      if (!this.rail || this.events) return;
      this.cards = Array.from(this.rail.querySelectorAll('[data-db-day]'));
      if (!this.cards.length) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.controls = this.querySelector('[data-db-controls]');
      this.previous = this.querySelector('[data-db-prev]');
      this.next = this.querySelector('[data-db-next]');
      this.current = this.querySelector('[data-db-current]');
      this.range = this.querySelector('[data-db-range]');
      this.today = this.querySelector('[data-db-today]');
      this.rtl = getComputedStyle(this.rail).direction === 'rtl';
      this.previous?.addEventListener('click', () => this.step(-1), options);
      this.next?.addEventListener('click', () => this.step(1), options);
      this.current?.addEventListener('click', () => this.goTo(this.today), options);
      this.rail.addEventListener('scroll', () => this.queueUpdate(), { ...options, passive: true });
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail || event.altKey || event.ctrlKey || event.metaKey) return;
        const arrows = { ArrowLeft: this.rtl ? 1 : -1, ArrowRight: this.rtl ? -1 : 1 };
        if (event.key in arrows) this.step(arrows[event.key]);
        else if (event.key === 'Home') this.goTo(this.cards[0]);
        else if (event.key === 'End') this.goTo(this.cards[this.cards.length - 1]);
        else return;
        event.preventDefault();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        this.goTo(event.target.closest('[data-db-day]'), 'auto');
      }, options);
      window.addEventListener('pageshow', () => this.checkDate(), options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.checkDate();
      }, options);
      if (window.ResizeObserver) {
        this.resize = new ResizeObserver(() => this.queueUpdate());
        this.resize.observe(this.rail);
      } else window.addEventListener('resize', () => this.queueUpdate(), options);
      if (this.controls) this.controls.hidden = false;
      this.setAttribute('data-db-ready', '');
      this.initialFrame = requestAnimationFrame(() => {
        // Let a Back/Forward return keep its saved horizontal position.
        const returning = performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward';
        if (!returning && !this.rail.scrollLeft && this.today) {
          const index = this.cards.indexOf(this.today);
          const showYesterday = this.rail.clientWidth >= this.today.offsetWidth * 2.8;
          this.goTo(this.cards[Math.max(0, index - (showYesterday ? 1 : 0))], 'auto');
        }
        this.updateControls();
      });
      this.checkDate();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.resize?.disconnect();
      clearTimeout(this.clockTimer);
      cancelAnimationFrame(this.initialFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
      this.refreshing = false;
    }

    queueUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateControls();
      });
    }

    visibleCards() {
      const rail = this.rail.getBoundingClientRect();
      return this.cards.filter(card => {
        const rect = card.getBoundingClientRect();
        return Math.min(rail.right, rect.right) - Math.max(rail.left, rect.left) > rect.width / 2;
      });
    }

    updateControls() {
      const visible = this.visibleCards();
      if (!visible.length) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      const atStart = Math.abs(this.rail.scrollLeft) <= 2;
      const atEnd = this.rail.scrollWidth - this.rail.clientWidth - Math.abs(this.rail.scrollLeft) <= 2;
      if (this.previous) this.previous.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      if (this.current) this.current.disabled = visible.includes(this.today);
      if (this.range) {
        const start = first.querySelector('time')?.textContent || first.dataset.dbDay;
        const range = first === last ? start : start + '–' + last.dataset.dbDay;
        if (range !== this.range.textContent) this.range.textContent = range;
      }
    }

    step(direction) {
      const first = this.visibleCards()[0] || this.cards[0];
      const count = Math.max(1, Math.floor(this.rail.clientWidth / (first.offsetWidth + parseFloat(getComputedStyle(this.rail).columnGap))));
      const index = Math.max(0, Math.min(this.cards.length - 1, this.cards.indexOf(first) + direction * count));
      this.goTo(this.cards[index]);
    }

    goTo(card, behavior) {
      if (!card) return;
      const edge = this.rtl ? 'right' : 'left';
      const distance = card.getBoundingClientRect()[edge] - this.rail.getBoundingClientRect()[edge];
      const motion = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.rail.scrollBy({ left: distance, behavior: behavior || motion });
    }

    checkDate() {
      clearTimeout(this.clockTimer);
      if (document.hidden || !this.isConnected) return;
      const parts = Object.fromEntries(detroitTime.formatToParts(new Date()).map(part => [part.type, part.value]));
      const month = parts.year + '-' + parts.month;
      const day = month === this.dataset.campaign ? Number(parts.day) : month < this.dataset.campaign ? 0 : 31;
      const untilMidnight = (86400 - ((parts.hour % 24) * 3600 + parts.minute * 60 + Number(parts.second))) * 1000 + 150;
      let delay = Math.min(untilMidnight, 6 * 3600 * 1000);
      if (day > Number(this.dataset.serverDay)) {
        // The server decides which offers exist. A device clock never unlocks a product.
        if (!this.refreshing && Date.now() >= (this.nextAttempt || 0)) this.refresh();
        delay = 60000;
      }
      this.clockTimer = setTimeout(() => this.checkDate(), delay);
    }

    async refresh() {
      this.refreshing = true;
      this.nextAttempt = Date.now() + 60000;
      const request = this.events.signal;
      try {
        const url = new URL(location.href);
        url.hash = '';
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('_db_refresh', String(Date.now()));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: request });
        if (!response.ok) return;
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newer = Array.from(page.querySelectorAll(tag)).find(section => section.dataset.sectionId === this.dataset.sectionId);
        if (!newer || newer.dataset.campaign !== this.dataset.campaign || Number(newer.dataset.serverDay) <= Number(this.dataset.serverDay)) return;
        if (this.isConnected && !request.aborted) {
          const focused = this.contains(document.activeElement);
          const replacement = document.importNode(newer, true);
          this.replaceWith(replacement);
          if (focused) replacement.querySelector('[data-db-rail]')?.focus({ preventScroll: true });
        }
      } catch {
        // An offline or background tab retries on return and after one minute.
      } finally {
        this.refreshing = false;
      }
    }
  }
  customElements.define(tag, NovemberDoorbusters);
})();
